import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { useFlags } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import {
	JIRA_WORK_ITEMS_CREATED_EVENT,
	JIRA_WORK_ITEMS_CREATE_FAILED_EVENT,
	JIRA_WORK_ITEMS_CREATING_EVENT,
	type JiraWorkItemCreatingDraft,
	Topics,
} from '@atlaskit/rovo-triggers/types';
import { token } from '@atlaskit/tokens';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

import type { DraftWorkItem } from './use-ai-create-project-context/types';
import { useCreateIssues } from './useCreateIssues';
import { useCreateIssuesSuccessFlags } from './useSuccessFlags';
import { useValidateIssuesToCreate } from './useValidateIssuesToCreate';
import type { IssueTypeToFieldsConfiguration } from './useValidateIssuesToCreate/types';

const messages = defineMessages({
	creationErrorTitle: {
		id: 'jira-create-work-items.create-button.error-flag.title',
		defaultMessage: "Couldn't create these work items",
		description: 'The title of the error flag when issue creation fails.',
	},
});

export type OnClickSuccessProps = {
	invocationIdToCreatedIssueKey: Record<string, string>;
	cloudUrl: string;
};

type CreatingDraftSource = {
	invocationId: string;
	summary: string;
	issueTypeId?: string;
	childWorkItems?: CreatingDraftSource[];
};

const resolveDraftIssueType = (
	{ issueTypeId }: CreatingDraftSource,
	issueTypeToFieldsConfigMap: IssueTypeToFieldsConfiguration,
): JiraWorkItemCreatingDraft['issueType'] => {
	if (!fg('aiwc_add_issuetype_icon_to_optimistic_updates')) {
		return undefined;
	}
	if (!issueTypeId) {
		return undefined;
	}
	const issueType = issueTypeToFieldsConfigMap[issueTypeId];
	if (!issueType?.avatarUrl) {
		return undefined;
	}
	return {
		id: issueTypeId,
		name: issueType.name,
		iconUrl: issueType.avatarUrl,
	};
};

const pickCreatingDraft = (
	source: CreatingDraftSource,
	issueTypeToFieldsConfigMap: IssueTypeToFieldsConfiguration,
): JiraWorkItemCreatingDraft => {
	const issueType = resolveDraftIssueType(source, issueTypeToFieldsConfigMap);
	return {
		invocationId: source.invocationId,
		summary: source.summary,
		...(issueType ? { issueType } : {}),
	};
};

/**
 * Flattens the validated draft work items into the shape broadcast on the `creating` event: each
 * parent followed by its (one level of) children, mirroring how `useCreateIssues` creates them.
 */
const toCreatingDrafts = (
	items: readonly CreatingDraftSource[],
	issueTypeToFieldsConfigMap: IssueTypeToFieldsConfiguration,
): JiraWorkItemCreatingDraft[] =>
	items.flatMap((item) => [
		pickCreatingDraft(item, issueTypeToFieldsConfigMap),
		...(item.childWorkItems ?? []).map((child) =>
			pickCreatingDraft(child, issueTypeToFieldsConfigMap),
		),
	]);

export type OnClickErrorProps = {
	errorMessage: string;
};

export const useCreateDraftWorkItems = ({
	onSuccess,
	onError,
	cloudId,
	cloudUrl = '',
	projectIdOrKey,
	showErrorsInFlag = true,
	showSuccessFlagOnCreate = true,
	isStagingArea,
	entryPoint,
	sendAifcAnalyticsEvent,
}: {
	onSuccess?: (args: OnClickSuccessProps) => void | Promise<void>;
	onError?: (args: OnClickErrorProps) => void;
	cloudId: string;
	cloudUrl: string;
	projectIdOrKey: string;
	showErrorsInFlag?: boolean;
	showSuccessFlagOnCreate?: boolean;
	isStagingArea: boolean;
	entryPoint?: string;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}): (({ draftWorkItems }: { draftWorkItems: DraftWorkItem[] }) => Promise<undefined>) => {
	const showSuccessFlag = useCreateIssuesSuccessFlags();
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const publish = usePublish(Topics.AI_MATE_ACTIONS);

	const { validateIssuesToCreate } = useValidateIssuesToCreate({
		cloudId,
		projectIdOrKey,
		sendAifcAnalyticsEvent,
	});

	const handleError = useCallback(
		(args: { shouldLogError: boolean; errorMessage: string }): void => {
			const { shouldLogError, errorMessage } = args;
			if (shouldLogError) {
				logException({
					exception: errorMessage,
					name: 'SendJiraDraftDataForCreateAllCommandError',
				});
			}

			if (showErrorsInFlag) {
				showFlag({
					id: 'jira-create-work-items.create-button.error-flag',
					// Title falls back to the default error message
					title: formatMessage(messages.creationErrorTitle),
					description: errorMessage,
					icon: <StatusErrorIcon color={token('color.icon.danger')} size="small" label="" />,
					isAutoDismiss: false,
				});
			}

			// The consumer's error handling
			onError?.({
				errorMessage,
			});
		},
		[formatMessage, showFlag, showErrorsInFlag, onError],
	);

	const { createIssues } = useCreateIssues({ cloudId, isStagingArea, entryPoint });

	return useCallback(
		async ({ draftWorkItems }: { draftWorkItems: DraftWorkItem[] }) => {
			// Correlates the `creating`/`created`/`failed` lifecycle events for this batch so
			// subscribers (e.g. the list view) can match the optimistic rows they rendered from the
			// drafts with the eventual result. Conceptually this is a per-batch id, distinct from the
			// per-suggestion ids carried on each draft (and keyed in `invocationIdToCreatedIssueKey`).
			// On the FE we derive it from the first draft's invocationId, which is unique per submission
			// and stable across the three publishes below.
			const batchInvocationId = draftWorkItems?.[0]?.invocationId;
			// Tracks whether we announced the batch via the `creating` event, so we only publish a
			// matching `failed` event (to revert optimistic rows) when there is something to revert.
			let hasAnnouncedCreating = false;

			try {
				const validationResult = await validateIssuesToCreate(draftWorkItems);

				if (!validationResult?.success || validationResult?.error) {
					handleError({
						shouldLogError: false,
						errorMessage: validationResult?.error ?? formatMessage(messages.creationErrorTitle),
					});
					return;
				} else {
					if (batchInvocationId) {
						publish({
							type: JIRA_WORK_ITEMS_CREATING_EVENT,
							source: 'jira-create-work-items',
							data: {
								invocationId: batchInvocationId,
								draftWorkItems: toCreatingDrafts(
									validationResult.context.draftWorkItems,
									validationResult.context.issueTypeToFieldsConfigMap,
								),
							},
						});
						hasAnnouncedCreating = true;
					}

					const result = await createIssues({
						draftWorkItems: validationResult?.context.draftWorkItems,
					});

					if (!result.isSuccess) {
						if (batchInvocationId) {
							publish({
								type: JIRA_WORK_ITEMS_CREATE_FAILED_EVENT,
								source: 'jira-create-work-items',
								data: { invocationId: batchInvocationId },
							});
						}

						// no issues were created
						handleError({
							shouldLogError: false,
							errorMessage: result.errorMessage ?? formatMessage(messages.creationErrorTitle),
						});
						return;
					}

					if (showSuccessFlagOnCreate) {
						const createdIssueKeys = Object.values(result.invocationIdToCreatedIssueKey);
						showSuccessFlag({ createdIssueKeys, cloudUrl });
					}

					if (batchInvocationId) {
						publish({
							type: JIRA_WORK_ITEMS_CREATED_EVENT,
							source: 'jira-create-work-items',
							data: {
								invocationId: batchInvocationId,
								createdIssueIds: result.createdIssueIds,
							},
						});
					}

					await onSuccess?.({
						invocationIdToCreatedIssueKey: result.invocationIdToCreatedIssueKey,
						cloudUrl,
					});
				}
			} catch (error) {
				if (batchInvocationId && hasAnnouncedCreating) {
					publish({
						type: JIRA_WORK_ITEMS_CREATE_FAILED_EVENT,
						source: 'jira-create-work-items',
						data: { invocationId: batchInvocationId },
					});
				}
				logException({
					exception: error,
					name: 'SendJiraDraftDataForCreateAllCommandError',
				});
				onError?.({
					errorMessage: formatMessage(messages.creationErrorTitle),
				});
				return undefined;
			}
		},
		[
			createIssues,
			formatMessage,
			handleError,
			onError,
			onSuccess,
			publish,
			showSuccessFlag,
			showSuccessFlagOnCreate,
			validateIssuesToCreate,
			cloudUrl,
		],
	);
};
